import { type FC, type ReactNode, useEffect, useState } from "react";

import OrderDetailsSummary from "./components/OrderDetailsSummary";
import OrderDetails from "./components/OrderDetails/OrderDetails";
import Spinner from "../../common/components/Spinner/Spinner";
import PageTitle from "~/common/components/PageTitle/v1/PageTitle";
import { orderDetailsMounted } from "~/feature/cart/cart/redux/actions/orderDetailsActions";
import { useDispatch } from "react-redux";
import { addBaseProductDataAction } from "../../redux/products/actions";
import ShipmentDetails from "./components/ShipmentDetails/ShipmentDetails";
import TabContext from "../../common/components/Tabs/TabContext";
import { Tabs } from "~/common/m3/Tabs/Tabs";
import { Tab } from "~/common/m3/Tabs/Tab";
import TabPanel from "../../common/components/Tabs/TabPanel";
import { fetchOrderDetails } from "../OrderListPage/api/fetchOrderDetails/fetchOrderDetails";
import { type DetailedOrderData } from "../common/modelsV2";
import { useTranslation } from "react-i18next";
import { type CaseDataObject } from "./components/ServiceCases/types";
import { fetchServiceCases } from "./components/ServiceCases/api";
import { Permission, userHasPermission } from "~/utils/permissions";
import {
  type FeedbackOptionCategory,
  OrderListFeedbackContextProvider,
  type UserData,
} from "~/OrdersQuotes/OrderDetailsPage/hooks/useSendOrderFeedback/components/OrderListFeedbackContext";
import { Alert } from "~/common/m3/Alert/Alert";

export function createOrderDetailsPage(data: { userData: UserData; orderFeedbackOptions: FeedbackOptionCategory[] }) {
  const params = new URLSearchParams(window.location.search);
  const soNumber = params.get("id") || undefined;

  return (
    <OrderListFeedbackContextProvider value={data}>
      <OrderDetailsPage soNumber={soNumber} />
    </OrderListFeedbackContextProvider>
  );
}

interface Props {
  soNumber?: string;
}

interface OrderDetailsDisplayState {
  loading: boolean;
  error: boolean;
  data?: DetailedOrderData;
  serviceCases?: Record<string, CaseDataObject[]>;
}

const OrderDetailsPage: FC<Props> = ({ soNumber }: Props) => {
  const [state, setState] = useState<OrderDetailsDisplayState>({
    loading: true,
    error: false,
  });

  const [activeTabIndex, setActiveTab] = useState<string>(TabId.ORDER_DETAILS);

  const missingId = !soNumber;
  const dispatch = useDispatch();

  useEffect(() => {
    if (missingId) {
      setState({
        loading: false,
        error: false,
      });
    } else {
      (async () => {
        try {
          let data;
          let serviceCases = {};
          if (userHasPermission(Permission.CAN_SEE_SERVICE_CASES)) {
            [data, serviceCases] = await Promise.all([fetchOrderDetails(soNumber!), fetchServiceCases(soNumber)]);
          } else {
            data = await fetchOrderDetails(soNumber!);
          }
          dispatch(addBaseProductDataAction(data.products.map((entry) => entry.product)));
          setState({
            data,
            serviceCases,
            loading: false,
            error: false,
          });
        } catch {
          setState({
            loading: false,
            error: true,
          });
        }
      })();
    }
  }, [soNumber]);

  useEffect(() => {
    dispatch(orderDetailsMounted());
  }, []);

  const { t } = useTranslation();
  const { loading, error } = state;

  let content: ReactNode | null = null;

  if (missingId) {
    const msg = t("orderDetailsPage.error.missingOrder");
    content = (
      <Alert className="mt-3" severity="error">
        {msg}
      </Alert>
    );
  } else if (loading) {
    content = <Spinner className="mt-3" />;
  } else if (error) {
    const msg = t("orderDetailsPage.error.general");
    content = (
      <Alert className="mt-3" severity="error">
        {msg}
      </Alert>
    );
  } else {
    const data = state.data!;
    const serviceCases = state.serviceCases;
    const hasDeliveryDetails = data.deliveryEntries && data.deliveryEntries.length > 0;

    const shipmentDetails = <ShipmentDetails data={data} />;
    const orderDetails = <OrderDetails data={data} serviceCases={serviceCases} />;

    content = (
      <>
        <div>
          <OrderDetailsSummary data={data} />
          {!hasDeliveryDetails && orderDetails}

          {hasDeliveryDetails && (
            <>
              <TabContext value={activeTabIndex + ""}>
                <Tabs
                  id="tabs"
                  onChange={(e, val) => {
                    setActiveTab(val);
                  }}
                  value={activeTabIndex}
                  fullWidthIndicator={false}
                  variant={"fullWidth"}
                >
                  <Tab value={TabId.ORDER_DETAILS} label={t("orderDetailsPage.title")} />
                  <Tab value={TabId.SHIPMENT_DETAILS} label={t("orderDetailsPage.shipmentDetails.title")} />
                </Tabs>

                <TabPanel value={TabId.ORDER_DETAILS} preserveChildren={true}>
                  {orderDetails}
                </TabPanel>

                <TabPanel value={TabId.SHIPMENT_DETAILS} preserveChildren={true}>
                  {shipmentDetails}
                </TabPanel>
              </TabContext>
            </>
          )}
        </div>
      </>
    );
  }

  return (
    <div className="scha__order-details-page">
      <div className="scha__order-details-page__content">
        <div className="scha__order-details-page__content__title scha__screen-only">
          <PageTitle title={t("orderDetailsPage.title")} />
        </div>
        {content}
      </div>
    </div>
  );
};

export default OrderDetailsPage;

enum TabId {
  ORDER_DETAILS = "Order Details",
  SHIPMENT_DETAILS = "Shipment Details",
}
